import {
  JOURNEY_TRIGGER_TYPES,
  JOURNEY_TRIGGER_TYPE_LABELS,
  type JourneyTrigger,
  type JourneyTriggerType,
  type FieldSpec,
} from "@ckgo2/shared";

const sel =
  "rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm";
const lbl = "block text-xs font-medium text-gray-500 mb-1";

/** Default trigger object for a freshly-picked type (keeps the union well-formed). */
function defaultTrigger(type: JourneyTriggerType, fields: FieldSpec[]): JourneyTrigger {
  const statusField = fields.find((f) => f.status);
  const dateField = fields.find((f) => f.date);
  const numField = fields.find((f) => f.type === "number" || f.type === "enum");
  switch (type) {
    case "status_change":
      return { type, to: statusField?.options?.[0] ? [statusField.options[0].value] : [] };
    case "field_change":
      return { type, field: numField?.key ?? statusField?.key ?? "status" };
    case "time_offset":
      return { type, dateField: dateField?.key ?? "createdAt", daysOffset: 0 };
    case "event":
      return { type, eventType: "" };
    case "inactivity":
      return { type, sinceField: dateField?.key ?? "createdAt", days: 14 };
    case "recurring":
      return { type, everyDays: 30 };
    case "manual":
      return { type };
  }
}

function StatusChecklist({
  options,
  selected,
  onChange,
}: {
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = selected.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            onClick={() =>
              onChange(on ? selected.filter((s) => s !== o.value) : [...selected, o.value])
            }
            className={
              "rounded-full border px-2.5 py-0.5 text-xs " +
              (on
                ? "bg-primary-600 border-primary-600 text-white"
                : "border-gray-300 bg-white text-gray-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300")
            }
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default function TriggerEditor({
  value,
  onChange,
  fields,
}: {
  value: JourneyTrigger;
  onChange: (t: JourneyTrigger) => void;
  fields: FieldSpec[];
}) {
  const statusOptions = fields.find((f) => f.status)?.options ?? [];
  const dateFields = fields.filter((f) => f.date);

  return (
    <div className="space-y-3">
      <div>
        <label className={lbl}>Trigger type</label>
        <select
          className={sel}
          value={value.type}
          onChange={(e) => onChange(defaultTrigger(e.target.value as JourneyTriggerType, fields))}
        >
          {JOURNEY_TRIGGER_TYPES.map((t) => (
            <option key={t} value={t}>
              {JOURNEY_TRIGGER_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
      </div>

      {value.type === "status_change" && (
        <div className="space-y-2">
          <div>
            <label className={lbl}>When status becomes (to)</label>
            <StatusChecklist
              options={statusOptions}
              selected={value.to}
              onChange={(to) => onChange({ ...value, to })}
            />
          </div>
          <div>
            <label className={lbl}>Only from (optional)</label>
            <StatusChecklist
              options={statusOptions}
              selected={value.from ?? []}
              onChange={(from) => onChange({ ...value, from: from.length ? from : undefined })}
            />
          </div>
        </div>
      )}

      {value.type === "field_change" && (
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className={lbl}>Field</label>
            <select
              className={sel}
              value={value.field}
              onChange={(e) => onChange({ ...value, field: e.target.value })}
            >
              {fields.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={lbl}>Equals value</label>
            <input
              className={sel}
              value={value.toValue != null ? String(value.toValue) : ""}
              placeholder="(or use ≥ / ≤)"
              onChange={(e) =>
                onChange({
                  ...value,
                  toValue: e.target.value || undefined,
                  gte: undefined,
                  lte: undefined,
                })
              }
            />
          </div>
          <div>
            <label className={lbl}>≥</label>
            <input
              type="number"
              className={sel + " w-20"}
              value={value.gte ?? ""}
              onChange={(e) =>
                onChange({
                  ...value,
                  gte: e.target.value === "" ? undefined : Number(e.target.value),
                  toValue: undefined,
                })
              }
            />
          </div>
          <div>
            <label className={lbl}>≤</label>
            <input
              type="number"
              className={sel + " w-20"}
              value={value.lte ?? ""}
              onChange={(e) =>
                onChange({
                  ...value,
                  lte: e.target.value === "" ? undefined : Number(e.target.value),
                  toValue: undefined,
                })
              }
            />
          </div>
        </div>
      )}

      {value.type === "time_offset" && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className={lbl}>Date field</label>
              <select
                className={sel}
                value={value.dateField}
                onChange={(e) => onChange({ ...value, dateField: e.target.value })}
              >
                {dateFields.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={lbl}>Days offset (− before, + after)</label>
              <input
                type="number"
                className={sel + " w-24"}
                value={value.daysOffset}
                onChange={(e) => onChange({ ...value, daysOffset: Number(e.target.value) || 0 })}
              />
            </div>
            <div>
              <label className={lbl}>At time (HH:MM, optional)</label>
              <input
                className={sel + " w-24"}
                placeholder="09:00"
                value={value.timeOfDay ?? ""}
                onChange={(e) => onChange({ ...value, timeOfDay: e.target.value || undefined })}
              />
            </div>
          </div>
          {statusOptions.length > 0 && (
            <div>
              <label className={lbl}>Only if status (optional)</label>
              <StatusChecklist
                options={statusOptions}
                selected={value.onlyIfStatus ?? []}
                onChange={(s) => onChange({ ...value, onlyIfStatus: s.length ? s : undefined })}
              />
            </div>
          )}
        </div>
      )}

      {value.type === "event" && (
        <div>
          <label className={lbl}>Event type</label>
          <input
            className={sel + " w-full"}
            placeholder="order.created, lead.converted, review.submitted, …"
            value={value.eventType}
            onChange={(e) => onChange({ ...value, eventType: e.target.value })}
          />
        </div>
      )}

      {value.type === "inactivity" && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className={lbl}>Inactive since field</label>
              <select
                className={sel}
                value={value.sinceField}
                onChange={(e) => onChange({ ...value, sinceField: e.target.value })}
              >
                {dateFields.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={lbl}>Days idle</label>
              <input
                type="number"
                className={sel + " w-20"}
                value={value.days}
                onChange={(e) => onChange({ ...value, days: Number(e.target.value) || 1 })}
              />
            </div>
          </div>
          {statusOptions.length > 0 && (
            <div>
              <label className={lbl}>Only if status (optional)</label>
              <StatusChecklist
                options={statusOptions}
                selected={value.onlyIfStatus ?? []}
                onChange={(s) => onChange({ ...value, onlyIfStatus: s.length ? s : undefined })}
              />
            </div>
          )}
        </div>
      )}

      {value.type === "recurring" && (
        <div>
          <label className={lbl}>Every N days</label>
          <input
            type="number"
            className={sel + " w-24"}
            value={value.everyDays ?? 30}
            onChange={(e) => onChange({ ...value, everyDays: Number(e.target.value) || 1 })}
          />
        </div>
      )}

      {value.type === "manual" && (
        <p className="text-sm text-gray-400">Enrolled only manually (no automatic trigger).</p>
      )}
    </div>
  );
}
